import type {
  DetailedHTMLProps,
  FC,
  HTMLAttributes,
  PropsWithChildren
} from 'react'

import { type TooltipProps } from 'antd'
import cn from 'classnames'

import { AppFlex } from '@web/shared/components/app-flex/ui/app-flex'
import { AppSkeleton } from '@web/shared/components/app-skeleton/ui/app-skeleton'
import McmTooltip from '@web/shared/components/mcm-tooltip/mcm-tooltip'
import type { TPanelProps } from '@web/shared/components/panel/model/types'

import styles from './panel.module.css'

type FormTitleProps = {
  className?: string
  tooltipProps?: TooltipProps | null
  withoutBorder?: boolean
  withMargin?: boolean
} & DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>

const Title: FC<PropsWithChildren<FormTitleProps>> = ({
  children,
  className,
  tooltipProps,
  withoutBorder,
  withMargin,
  ...props
}) => (
  <div
    className={cn(styles['title-container'], {
      [styles.withoutBorder]: withoutBorder,
      [styles.withMargin]: withMargin
    })}
  >
    {tooltipProps && (
      <McmTooltip className={cn(styles.tooltip)} {...tooltipProps} />
    )}
    <h4 className={cn(styles['title'], className || '')} {...props}>
      {children}
    </h4>
  </div>
)

type ContainerProps = DetailedHTMLProps<
  HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>
const Container: FC<PropsWithChildren<ContainerProps>> = ({
  children,
  ...props
}) => (
  <div className={styles['container-panels']} {...props}>
    {children}
  </div>
)

type ButtonsProps = {
  className?: string
  fullWidth?: boolean
}

const Buttons = ({
  fullWidth,
  className,
  children
}: PropsWithChildren<ButtonsProps>) => (
  <div
    className={cn(styles.buttons, className, { [styles.fullWidth]: fullWidth })}
  >
    {children}
  </div>
)

type DividerProps = {
  className?: string
}

const Divider: FC<DividerProps> = ({ className }) => (
  <div className={cn(styles.divider, className)} />
)

export const Panel = ({
  children,
  toolTip,
  className,
  variant = 'elevated',
  loading,
  height,
  width,
  skeletonCount = 3,
  ...props
}: TPanelProps) => (
  <AppFlex
    {...props}
    orientation='vertical'
    className={cn(styles['panel'], className, {
      [styles.ghost]: variant === 'ghost',
      [styles.elevated]: variant === 'elevated'
    })}
    style={{ height, width }}
  >
    {toolTip && <div className={styles.header}>{toolTip}</div>}
    {loading ? (
      <AppSkeleton.Container>
        {Array.from({ length: skeletonCount }, (_, idx) => (
          <AppSkeleton.FormItem key={idx} />
        ))}
      </AppSkeleton.Container>
    ) : (
      children
    )}
  </AppFlex>
)

Panel.Title = Title
Panel.Container = Container
Panel.Buttons = Buttons
Panel.Divider = Divider
